import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { logger } from '../utils/logger';
import {
  MessageSquare,
  AlertCircle,
  RefreshCcw,
  Search,
  CheckCircle,
  XCircle,
  Eye,
  ChevronLeft,
  ChevronRight,
  Download,
  Database,
  TrendingUp,
  Sparkles,
  Save,
  Edit3
} from 'lucide-react';
import MessageFormatter from './MessageFormatter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper component for stats cards
const StatsCard = ({ label, value, icon: Icon, color }) => (
  <Card className={`border-${color}-200 ${color === 'yellow' ? 'bg-yellow-50' : ''}`}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className={`text-2xl font-bold text-${color}-600`}>{value}</p>
        </div>
        <Icon className={`w-8 h-8 text-${color}-500`} />
      </div>
    </CardContent>
  </Card>
);

// Helper component for status badge
const StatusBadge = ({ item }) => {
  if (item.trained_response) {
    return (
      <Badge className="text-xs bg-yellow-100 text-yellow-800 flex items-center gap-1">
        <Sparkles className="w-3 h-3" />
        Trained
      </Badge>
    );
  }

  return (
    <Badge
      className={`text-xs ${
        item.is_confident
          ? 'bg-green-100 text-green-800'
          : 'bg-orange-100 text-orange-800'
      }`}
    >
      {item.is_confident ? 'Confident' : 'Low Confidence'}
    </Badge>
  );
};

// Helper component for reviewed badge
const ReviewedBadge = ({ item }) => {
  if (item.is_reviewed && !item.trained_response) {
    return (
      <Badge className="text-xs bg-purple-100 text-purple-800">
        Reviewed
      </Badge>
    );
  }
  return null;
};

// Helper component for feedback table row
const FeedbackRow = ({ item, onSelectItem, onDelete, truncateText }) => {
  const handleRowClick = () => onSelectItem(item);
  
  const handleDeleteClick = (e) => {
    e.stopPropagation();
    onDelete(item.id);
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    onSelectItem(item);
  };

  return (
    <tr
      className={`hover:bg-gray-50 cursor-pointer ${item.trained_response ? 'bg-yellow-50' : ''}`}
      onClick={handleRowClick}
    >
      <td className="px-4 py-3">
        <p className="text-sm text-gray-900 max-w-xs truncate">
          {truncateText(item.question, 80)}
        </p>
      </td>
      <td className="px-4 py-3">
        <p className="text-sm text-gray-600 max-w-md truncate">
          {item.trained_response
            ? truncateText(item.trained_response, 100)
            : truncateText(item.response, 100)
          }
        </p>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-col gap-1">
          <StatusBadge item={item} />
          <ReviewedBadge item={item} />
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
        {formatDate(item.created_at)}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm"
            variant="outline"
            onClick={handleEditClick}
            className="text-yellow-600 hover:bg-yellow-50"
            title="Train this response"
          >
            <Edit3 className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-red-600 hover:bg-red-50"
            onClick={handleDeleteClick}
          >
            <XCircle className="w-4 h-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
};

// Helper component for table content
const FeedbackTable = ({ loading, feedback, onSelectItem, onDelete, truncateText }) => {
  if (loading) {
    return (
      <tr>
        <td colSpan="5" className="px-4 py-8 text-center">
          <RefreshCcw className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500">Loading feedback...</p>
        </td>
      </tr>
    );
  }

  if (feedback.length === 0) {
    return (
      <tr>
        <td colSpan="5" className="px-4 py-8 text-center">
          <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500">No feedback found</p>
          <p className="text-sm text-gray-400 mt-1">
            Click "Import from Chats" to populate feedback from existing conversations
          </p>
        </td>
      </tr>
    );
  }

  return feedback.map((item) => (
    <FeedbackRow
      key={item.id}
      item={item}
      onSelectItem={onSelectItem}
      onDelete={onDelete}
      truncateText={truncateText}
    />
  ));
};

// Helper component for pagination
const Pagination = ({ currentPage, totalPages, itemsPerPage, totalItems, onPageChange }) => {
  if (totalPages <= 1) return null;

  const showingFrom = ((currentPage - 1) * itemsPerPage) + 1;
  const showingTo = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t">
      <p className="text-sm text-gray-500">
        Showing {showingFrom} to {showingTo} of {totalItems}
      </p>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="text-sm text-gray-600">
          Page {currentPage} of {totalPages}
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// Helper component for detail modal header
const DetailModalHeader = ({ selectedItem, onClose }) => (
  <div className="p-6 border-b flex items-center justify-between">
    <h3 className="text-xl font-bold text-gray-900">Train Bot Response</h3>
    <div className="flex items-center gap-2">
      {selectedItem.trained_response ? (
        <Badge className="bg-yellow-100 text-yellow-800 flex items-center gap-1">
          <Sparkles className="w-3 h-3" />
          Trained
        </Badge>
      ) : (
        <Badge
          className={
            selectedItem.is_confident
              ? 'bg-green-100 text-green-800'
              : 'bg-orange-100 text-orange-800'
          }
        >
          {selectedItem.is_confident ? 'Confident' : 'Low Confidence'}
        </Badge>
      )}
      <button
        onClick={onClose}
        className="text-gray-400 hover:text-gray-600 p-2"
      >
        <XCircle className="w-5 h-5" />
      </button>
    </div>
  </div>
);

// Helper component for training section
const TrainingSection = ({
  trainedResponse,
  setTrainedResponse,
  onSaveTraining,
  onRemoveTraining,
  isTraining,
  hasExistingTraining
}) => (
  <div className="bg-yellow-50 p-4 rounded-xl border-2 border-yellow-200">
    <h4 className="text-sm font-semibold text-yellow-800 mb-3 flex items-center gap-2">
      <Sparkles className="w-4 h-4" />
      Train Correct Response
    </h4>
    <p className="text-xs text-yellow-700 mb-3">
      Enter the correct response below. The bot will automatically use this response when users ask similar questions.
    </p>
    <textarea
      value={trainedResponse}
      onChange={(e) => setTrainedResponse(e.target.value)}
      placeholder="Enter the correct response for this question... (supports markdown formatting)"
      className="w-full h-40 p-3 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm resize-none"
    />
    <div className="flex items-center gap-3 mt-3">
      <Button
        onClick={onSaveTraining}
        disabled={isTraining || !trainedResponse.trim()}
        className="bg-yellow-500 hover:bg-yellow-600 text-white flex items-center gap-2"
      >
        {isTraining ? (
          <RefreshCcw className="w-4 h-4 animate-spin" />
        ) : (
          <Save className="w-4 h-4" />
        )}
        {isTraining ? 'Saving...' : 'Save Training'}
      </Button>
      {hasExistingTraining && (
        <Button
          onClick={onRemoveTraining}
          variant="outline"
          className="text-red-600 hover:bg-red-50"
        >
          Remove Training
        </Button>
      )}
    </div>
  </div>
);

// Helper component for detail modal
const DetailModal = ({
  selectedItem,
  trainedResponse,
  setTrainedResponse,
  onSaveTraining,
  onRemoveTraining,
  onMarkReviewed,
  onClose,
  isTraining
}) => {
  if (!selectedItem) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <DetailModalHeader selectedItem={selectedItem} onClose={onClose} />

        <div className="p-6 space-y-6">
          {/* Question */}
          <div>
            <h4 className="text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
              User Question
            </h4>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
              <p className="text-gray-800">{selectedItem.question}</p>
            </div>
          </div>

          {/* Original Bot Response */}
          <div>
            <h4 className="text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${
                selectedItem.is_confident ? 'bg-green-500' : 'bg-orange-500'
              }`}></span>
              Original Bot Response
            </h4>
            <div className={`p-4 rounded-lg border ${
              selectedItem.is_confident
                ? 'bg-green-50 border-green-100'
                : 'bg-orange-50 border-orange-100'
            }`}>
              <div className="text-gray-800 text-sm">
                <MessageFormatter content={selectedItem.response} />
              </div>
            </div>
          </div>

          {/* Training Section */}
          <TrainingSection
            trainedResponse={trainedResponse}
            setTrainedResponse={setTrainedResponse}
            onSaveTraining={onSaveTraining}
            onRemoveTraining={onRemoveTraining}
            isTraining={isTraining}
            hasExistingTraining={!!selectedItem.trained_response}
          />

          {/* Preview of trained response */}
          {trainedResponse.trim() && (
            <div>
              <h4 className="text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Preview
              </h4>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="text-gray-800 text-sm">
                  <MessageFormatter content={trainedResponse} />
                </div>
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="flex flex-wrap gap-4 text-sm text-gray-500 pt-4 border-t">
            <span>Session: {selectedItem.session_id?.substring(0, 12)}...</span>
            <span>Date: {formatDate(selectedItem.created_at)}</span>
            {selectedItem.is_reviewed && <span className="text-purple-600">✓ Reviewed</span>}
          </div>
        </div>

        <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => onMarkReviewed(selectedItem.id, !selectedItem.is_reviewed)}
          >
            {selectedItem.is_reviewed ? 'Unmark as Reviewed' : 'Mark as Reviewed'}
          </Button>
          <Button onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

// Helper function outside component
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const Feedback = () => {
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isMigrating, setIsMigrating] = useState(false);
  const [isTraining, setIsTraining] = useState(false);
  const [trainedResponse, setTrainedResponse] = useState('');
  const [trainedCount, setTrainedCount] = useState(0);
  const itemsPerPage = 20;

  const fetchStats = async () => {
    try {
      const response = await api.get(`${API}/feedback/stats`);
      setStats(response.data);
    } catch (error) {
      logger.error('Error fetching stats:', error);
    }
  };

  const fetchFeedback = async () => {
    setLoading(true);
    try {
      const skip = (currentPage - 1) * itemsPerPage;
      const params = new URLSearchParams({
        limit: itemsPerPage.toString(),
        skip: skip.toString(),
        filter_type: filterType
      });

      if (searchQuery) {
        params.append('search', searchQuery);
      }

      const response = await api.get(`${API}/feedback?${params}`);
      setFeedback(response.data.items || []);
      setTotalItems(response.data.total || 0);
    } catch (error) {
      logger.error('Error fetching feedback:', error);
      setFeedback([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMigrate = async () => {
    if (!window.confirm('This will import Q&A pairs from chat history into the feedback table. Continue?')) {
      return;
    }

    setIsMigrating(true);
    try {
      const response = await api.post(`${API}/feedback/migrate`);
      alert(response.data.message);
      fetchFeedback();
      fetchStats();
    } catch (error) {
      logger.error('Error migrating:', error);
      alert('Migration failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsMigrating(false);
    }
  };

  const handleMarkReviewed = async (id, isReviewed) => {
    try {
      await api.put(`${API}/feedback/${id}`, { is_reviewed: isReviewed });
      fetchFeedback();
      fetchStats();
    } catch (error) {
      logger.error('Error updating feedback:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this feedback entry?')) return;

    try {
      await api.delete(`${API}/feedback/${id}`);
      fetchFeedback();
      fetchStats();
    } catch (error) {
      logger.error('Error deleting feedback:', error);
    }
  };

  const fetchTrainedCount = async () => {
    try {
      const response = await api.get(`${API}/feedback/trained`);
      setTrainedCount(response.data.total || 0);
    } catch (error) {
      logger.error('Error fetching trained count:', error);
    }
  };

  const handleSaveTraining = async () => {
    if (!selectedItem || !trainedResponse.trim()) return;

    setIsTraining(true);
    try {
      await api.put(`${API}/feedback/${selectedItem.id}`, {
        trained_response: trainedResponse.trim(),
        is_reviewed: true
      });

      // Update the selected item locally
      setSelectedItem(prev => ({
        ...prev,
        trained_response: trainedResponse.trim(),
        is_reviewed: true
      }));

      fetchFeedback();
      fetchStats();
      fetchTrainedCount();
      alert('Training saved! The bot will now use this response for similar questions.');
    } catch (error) {
      logger.error('Error saving training:', error);
      alert('Failed to save training: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsTraining(false);
    }
  };

  const handleRemoveTraining = async () => {
    if (!selectedItem) return;
    if (!window.confirm('Remove the trained response? The bot will use RAG for this question again.')) return;

    try {
      await api.put(`${API}/feedback/${selectedItem.id}`, {
        trained_response: null
      });

      setSelectedItem(prev => ({ ...prev, trained_response: null }));
      setTrainedResponse('');
      fetchFeedback();
      fetchTrainedCount();
      alert('Training removed.');
    } catch (error) {
      logger.error('Error removing training:', error);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchTrainedCount();
  }, []);

  useEffect(() => {
    fetchFeedback();
  }, [currentPage, filterType]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchFeedback();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '-';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const exportToCSV = () => {
    const headers = ['Question', 'Response', 'Confident', 'Reviewed', 'Date', 'Session ID'];
    const rows = feedback.map(item => [
      `"${(item.question || '').replace(/"/g, '""')}"`,
      `"${(item.response || '').replace(/"/g, '""')}"`,
      item.is_confident ? 'Yes' : 'No',
      item.is_reviewed ? 'Yes' : 'No',
      formatDate(item.created_at),
      item.session_id || ''
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `feedback-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // When selecting an item, load its trained response if exists
  const handleSelectItem = (item) => {
    setSelectedItem(item);
    setTrainedResponse(item.trained_response || '');
  };

  const handleCloseModal = () => {
    setSelectedItem(null);
  };

  const handleFilterChange = (value) => {
    setFilterType(value);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <StatsCard label="Total Q&A" value={stats?.total || 0} icon={MessageSquare} color="blue" />
        <StatsCard label="Confident" value={stats?.confident || 0} icon={CheckCircle} color="green" />
        <StatsCard label="Low Confidence" value={stats?.low_confidence || 0} icon={AlertCircle} color="orange" />
        <StatsCard label="Reviewed" value={stats?.reviewed || 0} icon={Eye} color="purple" />
        <StatsCard label="Confidence Rate" value={`${stats?.confidence_rate || 0}%`} icon={TrendingUp} color="teal" />
        <StatsCard label="Trained" value={trainedCount} icon={Sparkles} color="yellow" />
      </div>

      {/* Filters and Actions */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex flex-1 gap-2 w-full md:w-auto">
              {/* Search */}
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="text"
                  placeholder="Search questions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Filter Dropdown */}
              <select
                value={filterType}
                onChange={(e) => handleFilterChange(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All</option>
                <option value="confident">Confident</option>
                <option value="low_confidence">Low Confidence</option>
                <option value="reviewed">Reviewed</option>
                <option value="unreviewed">Unreviewed</option>
                <option value="trained">Trained</option>
              </select>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleMigrate}
                disabled={isMigrating}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <Database className={`w-4 h-4 ${isMigrating ? 'animate-spin' : ''}`} />
                {isMigrating ? 'Migrating...' : 'Import from Chats'}
              </Button>

              <Button
                onClick={fetchFeedback}
                disabled={loading}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>

              <Button
                onClick={exportToCSV}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Question
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Response
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <FeedbackTable
                  loading={loading}
                  feedback={feedback}
                  onSelectItem={handleSelectItem}
                  onDelete={handleDelete}
                  truncateText={truncateText}
                />
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={totalItems}
            onPageChange={setCurrentPage}
          />
        </CardContent>
      </Card>

      {/* Detail Modal with Training */}
      <DetailModal
        selectedItem={selectedItem}
        trainedResponse={trainedResponse}
        setTrainedResponse={setTrainedResponse}
        onSaveTraining={handleSaveTraining}
        onRemoveTraining={handleRemoveTraining}
        onMarkReviewed={handleMarkReviewed}
        onClose={handleCloseModal}
        isTraining={isTraining}
      />
    </div>
  );
};

export default Feedback;